// Resolves a Zoom "Next Steps" assignee display name to a CRM identity, scoped to the
// meeting's participant roster. Zoom summaries give names only (no emails), so the
// roster — whose rows were already matched to a sales rep (by email) or a contact (by
// email, then name) at ingestion — is the trustworthy source: only people who actually
// attended can be assigned, and their identity is read off the row rather than
// re-guessed against the whole sales/contacts tables (which mis-fires when a contact
// shares a name with a sales rep).
//
// Internal/external is derived from the binding, never from the name: a sales_id means
// internal, a contact_id means external, and anything the roster can't pin to exactly
// one identity is left unresolved for the user to pick.
//
// Duplicated in supabase/functions/zoom_webhook/resolveAssignee.ts (Deno can't import
// from src/) — mirrors the existing parseTasksFromSummary duplication. Keep in sync.

// ids are `string | number` because that is ra-core's Identifier, which is what
// ZoomMeetingParticipant carries. Spelled out rather than imported: this module is
// duplicated into the Deno edge function, which cannot import from ra-core.
export type ResolverParticipant = {
  name?: string | null;
  user_email?: string | null;
  sales_id?: string | number | null;
  contact_id?: string | number | null;
};

export type AssigneeBinding =
  | { kind: "sales"; sales_id: string | number }
  | {
      kind: "contact";
      contact_id: string | number;
      name?: string | null;
      email?: string | null;
    }
  // On the roster, but not a teammate and not in the CRM. Somebody outside the
  // company all the same: nothing they committed to is ours to do, and we hold
  // no record to point at. Distinct from "unresolved", which means we could not
  // work out who was meant at all.
  | { kind: "external"; name?: string | null; email?: string | null }
  | { kind: "unresolved" };

const UNRESOLVED: AssigneeBinding = { kind: "unresolved" };

// A participant matches a token when one of its name parts equals the token
// (case-insensitive, whole word) — same rule the old matchParticipantByName used.
function participantMatchesToken(
  p: ResolverParticipant,
  token: string,
): boolean {
  if (!p.name) return false;
  return p.name.split(/\s+/).some((part) => part.toLowerCase() === token);
}

// Identity fingerprint so two roster rows for the same person collapse to one match.
function identityKey(p: ResolverParticipant): string | null {
  if (p.sales_id != null) return `s${p.sales_id}`;
  if (p.contact_id != null) return `c${p.contact_id}`;
  return null;
}

// Find the single roster participant a Next-Steps name refers to — REGARDLESS of whether
// that participant resolved to a CRM identity. This is the "attendee bridge": an
// unresolved task name ("Josh") maps to the attendee's fuller phone name ("Josh Kerr") +
// email, which are far better keys for suggesting a contact than the bare task word.
// Returns null when nothing matches or when the name is ambiguous across distinct people.
export function matchAttendee(
  assigneeName: string | undefined | null,
  roster: ResolverParticipant[],
): ResolverParticipant | null {
  const raw = (assigneeName ?? "").trim();
  if (!raw || roster.length === 0) return null;

  const tokens = raw
    .split("/")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return null;

  const matched = roster.filter((p) =>
    tokens.some((t) => participantMatchesToken(p, t)),
  );
  if (matched.length === 0) return null;

  // Collapse rows for the same person (by identity, else by name). Two DISTINCT people
  // matching the same name is ambiguous — don't guess which attendee is meant.
  const personKey = (p: ResolverParticipant) =>
    identityKey(p) ?? (p.name ?? "").trim().toLowerCase();
  const distinct = new Set(matched.map(personKey));
  if (distinct.size !== 1) return null;

  return matched[0];
}

export function resolveAssignee(
  assigneeName: string | undefined | null,
  roster: ResolverParticipant[],
): AssigneeBinding {
  const raw = (assigneeName ?? "").trim();
  if (!raw || roster.length === 0) return UNRESOLVED;

  // Zoom sometimes joins collaborators with "/" ("John/Jane") — match any token.
  const tokens = raw
    .split("/")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return UNRESOLVED;

  // Roster participants matching a token, WHETHER OR NOT they carry a CRM identity.
  // This used to require an identity, which quietly made "attended the call but is
  // not in our database" indistinguishable from "no idea who this is". It is not the
  // same thing: the first is somebody we watched join the meeting. Production says
  // that is nearly every outside attendee, and each one produced a needs-assignment
  // row that blocked the meeting behind a question with no correct answer.
  const matched = roster.filter((p) =>
    tokens.some((t) => participantMatchesToken(p, t)),
  );
  if (matched.length === 0) return UNRESOLVED;

  // Bind only when every match points at the SAME person. Two distinct people
  // (e.g. two attendees both named "Sang") is ambiguous → leave it for the user.
  // Identity first, falling back to the name for anyone the CRM has no row for,
  // so two different unknown attendees still read as ambiguous.
  const personKey = (p: ResolverParticipant) =>
    identityKey(p) ?? (p.name ?? "").trim().toLowerCase();
  const distinct = new Set(matched.map(personKey));
  if (distinct.size !== 1) return UNRESOLVED;

  const p = matched[0];
  if (p.sales_id != null) return { kind: "sales", sales_id: p.sales_id };
  if (p.contact_id != null) {
    return {
      kind: "contact",
      contact_id: p.contact_id,
      name: p.name ?? undefined,
      email: p.user_email ?? undefined,
    };
  }
  return {
    kind: "external",
    name: p.name ?? undefined,
    email: p.user_email ?? undefined,
  };
}
